import { favoriteFiltersListName } from "src/api/config";
import { deleteItem } from "src/modules/spItems/actions";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectItems } from "src/modules/spItems/selectors";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { TrashIcon } from "src/pages/Reports/shared/icons";
import Checkbox from "src/pages/Reports/WorkPlaceReport/components/Checkbox";
import { FiltersStateType } from "src/pages/DashboardPage/acadia/type";

type Props = {
  opened: boolean;
  onApplyFilter: (filterId: string) => void;
  onUpdateDefault: (filterId: string, checked: boolean) => void;
  currentFavoriteId: string | null;
};

type FilterRow = { label: string; value: string };

function formatDate(d: Date | string | null): string {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function extractFilterRows(parsed: FiltersStateType): FilterRow[] {
  const rows: FilterRow[] = [];

  const checkboxFields: { key: keyof FiltersStateType; label: string }[] = [
    { key: "collections", label: "Коллекция" },
    { key: "departments", label: "Подразделение/Функция" },
    { key: "sectors", label: "Колонна/Направление/Отдел" },
    { key: "processes", label: "Блок колонны/Участок" },
    { key: "types", label: "Тип документа" },
    { key: "lines", label: "Линия розлива" },
    { key: "critical", label: "Критичность" },
    { key: "workstations", label: "Рабочее место" },
    { key: "titleEquipment", label: "Название оборудования" },
    { key: "manufacturer", label: "Производитель оборудования" },
    { key: "model", label: "Модель оборудования" },
    { key: "typeEquipment", label: "Тип оборудования" },
    { key: "sap", label: "SAP № технического места" },
  ];

  for (const { key, label } of checkboxFields) {
    const arr = parsed[key] as { title: string }[] | undefined;
    if (arr?.length) {
      rows.push({ label, value: arr.map((i) => i.title).join(", ") });
    }
  }

  const userFields: { key: keyof FiltersStateType; label: string }[] = [
    { key: "responsibleForRevision", label: "Ответственный за пересмотр" },
    { key: "created", label: "Создатель" },
  ];

  for (const { key, label } of userFields) {
    const arr = parsed[key] as { title: string }[] | undefined;
    if (arr?.length) {
      rows.push({ label, value: arr.map((u) => u.title).join(", ") });
    }
  }

  const date = parsed.dateForRevision;
  if (date) {
    const parts: string[] = [];
    if (date.required) parts.push("Требует пересмотра");
    if (date.date?.start) parts.push(`с ${formatDate(date.date.start)}`);
    if (date.date?.end) parts.push(`по ${formatDate(date.date.end)}`);
    if (parts.length) {
      rows.push({ label: "Пересмотр документа", value: parts.join(" ") });
    }
  }

  return rows;
}

function DocBundlesModal({ opened, onApplyFilter, onUpdateDefault, currentFavoriteId }: Props) {
  const dispatch = useAppDispatch();
  const favoriteFiltersData = useAppSelector((state) => selectItems(state, favoriteFiltersListName));

  const onDelete = (id: string) => {
    dispatch(deleteItem({ listName: favoriteFiltersListName, ID: id, updateReducer: true }));
  };

  if (!opened || !favoriteFiltersData?.length) return null;

  return (
    <FlexCol className="absolute w-[95%] tablet:w-[600px] gap-2.5 right-0 top-[76px] z-20 h-[calc(100dvh-76px)] overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB]">
      {favoriteFiltersData.map((item) => {
        const parsed = JSON.parse(item.DocumentsFilter) as FiltersStateType;
        const rows = extractFilterRows(parsed);
        const id = item.ID.toString();

        return (
          <div key={id} className="shadow-[0_10px_15px_0_rgba(5,_13,_29,_0.18)] p-5 bg-white rounded-lg">
            <FlexRow className="justify-between items-center mb-4">
              <Checkbox checked={id === currentFavoriteId} onChange={() => onApplyFilter(id)} title={item.Title} />
              <button onClick={() => onDelete(id)}>
                <TrashIcon />
              </button>
            </FlexRow>
            <FlexCol className="pl-7 gap-2.5">
              {rows.map(({ label, value }) => (
                <span key={label}>
                  {label}: <span className="text-black">{value}</span>
                </span>
              ))}
              <Checkbox
                checked={item.isDefault || false}
                onChange={(checked) => onUpdateDefault(id, checked)}
                title="Установить по умолчанию"
                classNameText="text-[14px] tablet:text-base"
              />
            </FlexCol>
          </div>
        );
      })}
    </FlexCol>
  );
}

export default DocBundlesModal;
